/**
 * Container for the MoneyInput component.
 *
 * Manages M/K unit state and draft input value. Saves on Enter,
 * cancels on Escape, saves on blur (with debounce to protect M/K toggle).
 * Converts between display value and raw units.
 * @module components/MoneyInput/MoneyInputContainer
 */

import React, { memo, useCallback, useMemo, useRef, useState } from "react";
import type {
  NativeSyntheticEvent,
  TextInputKeyPressEventData,
} from "react-native";

import MoneyInputView, { type MoneyUnit } from "./MoneyInputView";

/** Size variant for MoneyInput rendering */
export type MoneyInputVariant = "compact" | "expanded";

/** Props for {@link MoneyInputContainer} */
export interface MoneyInputProps {
  /** Current value in raw units (e.g. 5000000 for €5M) */
  readonly value: number | null;
  /** Called with the new raw value when the user confirms (Enter) */
  readonly onConfirm: (value: number | null) => void;
  /** Called with the raw value on every keystroke (for parent state sync) */
  readonly onChange?: (value: number | null) => void;
  /** Called when the user cancels (Escape) */
  readonly onCancel: () => void;
  /** Whether the input is disabled */
  readonly isDisabled?: boolean;
  /** Placeholder text */
  readonly placeholder?: string;
  /** Size variant: "compact" for inline table cells, "expanded" for modals */
  readonly variant?: MoneyInputVariant;
}

/**
 * Derives the default unit from a raw value.
 * @param value - Raw numeric value
 * @returns The appropriate default unit
 */
function deriveUnit(value: number | null): MoneyUnit {
  if (value == null || value === 0) return "M";
  return Math.abs(value) < 1_000_000 ? "K" : "M";
}

/**
 * Converts a raw value to display string for a given unit.
 * @param value - Raw numeric value
 * @param unit - Display unit
 * @returns Display string
 */
function toDisplay(value: number | null, unit: MoneyUnit): string {
  if (value == null) return "";
  const divisor = unit === "M" ? 1_000_000 : 1_000;
  const result = value / divisor;
  return String(parseFloat(result.toFixed(2)));
}

/**
 * Parses draft text to raw value.
 * @param text - Draft input text
 * @param unit - Current unit
 * @returns Raw value or null
 */
function parseToRaw(text: string, unit: MoneyUnit): number | null {
  if (text === "" || text === ".") return null;
  const num = parseFloat(text);
  if (Number.isNaN(num)) return null;
  const multiplier = unit === "M" ? 1_000_000 : 1_000;
  return Math.round(num * multiplier);
}

/** Debounce delay for blur-to-save (ms). Allows M/K toggle clicks to cancel. */
const BLUR_SAVE_DELAY = 150;

/**
 * Container that manages MoneyInput draft state.
 * Saves on Enter or blur, cancels on Escape.
 * @param props - Component props
 * @param props.value - Current raw value
 * @param props.onConfirm - Confirm handler
 * @param props.onChange - Called with raw value on every keystroke
 * @param props.onCancel - Cancel handler
 * @param props.isDisabled - Whether input is disabled
 * @param props.placeholder - Placeholder text
 * @param props.variant - Size variant: "compact" for table cells, "expanded" for modals
 */
const MoneyInputContainer = ({
  value,
  onConfirm,
  onChange,
  onCancel,
  isDisabled,
  placeholder,
  variant = "compact",
}: MoneyInputProps) => {
  const [unit, setUnit] = useState<MoneyUnit>(() => deriveUnit(value));
  const initialDisplay = useMemo(() => toDisplay(value, unit), [value, unit]);
  const [draft, setDraft] = useState(initialDisplay);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleChangeText = useCallback(
    (text: string) => {
      // Strip anything that's not a digit or dot
      const cleaned = text.replace(/[^0-9.]/g, "");
      // Reject if a dot is followed by another dot
      if (cleaned.includes("..")) return;
      // Reject multiple dots
      if (cleaned.split(".").length > 2) return;
      setDraft(cleaned);
      onChange?.(parseToRaw(cleaned, unit));
    },
    [onChange, unit]
  );

  const handleSubmit = useCallback(() => {
    clearTimeout(blurTimeoutRef.current);
    onConfirm(parseToRaw(draft, unit));
  }, [draft, unit, onConfirm]);

  /** Save on blur with a short delay so M/K toggle clicks can cancel it. */
  const handleBlur = useCallback(() => {
    blurTimeoutRef.current = setTimeout(() => {
      onConfirm(parseToRaw(draft, unit));
    }, BLUR_SAVE_DELAY);
  }, [draft, unit, onConfirm]);

  const handleKeyPress = useCallback(
    (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
      if (e.nativeEvent.key === "Escape") {
        clearTimeout(blurTimeoutRef.current);
        onCancel();
      }
    },
    [onCancel]
  );

  const handleSelectM = useCallback(() => {
    clearTimeout(blurTimeoutRef.current);
    const raw = parseToRaw(draft, unit);
    setUnit("M");
    setDraft(raw != null ? toDisplay(raw, "M") : "");
  }, [draft, unit]);

  const handleSelectK = useCallback(() => {
    clearTimeout(blurTimeoutRef.current);
    const raw = parseToRaw(draft, unit);
    setUnit("K");
    setDraft(raw != null ? toDisplay(raw, "K") : "");
  }, [draft, unit]);

  return (
    <MoneyInputView
      displayValue={draft}
      unit={unit}
      onChangeText={handleChangeText}
      onSelectM={handleSelectM}
      onSelectK={handleSelectK}
      onSubmit={handleSubmit}
      onBlur={handleBlur}
      onCancel={onCancel}
      onKeyPress={handleKeyPress}
      isDisabled={isDisabled}
      placeholder={placeholder}
      variant={variant}
    />
  );
};

MoneyInputContainer.displayName = "MoneyInputContainer";

export default memo(MoneyInputContainer);
